import { CommonDialogs } from "@mcleod/common";
import { CrudDecorator, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, Layout, Snackbar, Switch, TableContentsChangedEvent, TableRow, TableRowDisplayEvent, TableRowMoveEvent, Textbox } from "@mcleod/components";
import { Alignment, Api, Navigation, StringUtil, UrlUtil } from "@mcleod/core";
import { AutogenLayoutMobileCommForm } from "./autogen/AutogenLayoutMobileCommForm";
import { FormFieldRenderer } from "./FormFieldRenderer";
import { MobileCommExAppControl } from "./MobileCommExAppControl";

/**
 * Constants for field types and values
 */
const FIELD_TYPES = {
    CAP: "CAP"
} as const;

const FIELD_VALUES = {
    CAPTION: "Caption",
    BILL_OF_LADING_ON_STOP: "Bill of Lading on Stop"
} as const;

const FORM_FIELD_RENDERER_CONFIG = {
    SHOW_FORM_DETAILS_LABEL: true,
    TEXTBOX_WIDTH: 120
} as const;

const COMPONENT_IDS = {
    TEXTBOX_FORM_FIELD_NAME: "textboxFormFieldName",
    TEXTBOX_FIELD_TYPE: "textboxFieldType",
    TEXTBOX_FIELD_VALUE: "textboxFieldValue",
    TEXTBOX_FIELD_LENGTH: "textboxFieldLength",
    TEXTBOX_ADDITIONAL_VALUE: "textboxAdditionalValue",
    SWITCH_ROW_BREAK: "switchRowBreak"
} as const;

/**
 * Form for managing mobile communication configurations and field layouts
 */
export class MobileCommForm extends AutogenLayoutMobileCommForm {
    private formFieldRenderer: FormFieldRenderer;
    private _editClickListener: any;

    /**
     * Initializes the form and sets up event listeners
     */
    async onLoad(): Promise<void> {
        this.initializeFormFieldRenderer();
        this.setupEventListeners();
    }

    /**
     * Initializes the form field renderer with default configuration
     */
    private initializeFormFieldRenderer(): void {
        this.formFieldRenderer = new FormFieldRenderer({
            showFormDetailsLabel: FORM_FIELD_RENDERER_CONFIG.SHOW_FORM_DETAILS_LABEL,
            textboxWidth: FORM_FIELD_RENDERER_CONFIG.TEXTBOX_WIDTH
        });
    }

    /**
     * Sets up event listeners for table contents changes
     */
    private setupEventListeners(): void {
        this.tableFormFieldOpt.addContentsChangedListener((event: TableContentsChangedEvent) => {
            this.generateFormDetails();
        });
    }

    // Table Event Handlers

    /**
     * Handles table row move events by updating data and regenerating form details
     */
    tableFormFieldOptAfterTableRowMove(event: TableRowMoveEvent): void {
        this.updateFormFieldRowData();
        this.generateFormDetails();
        this.groupAndValidateTableRows();
    }

    /**
     * Handles table contents change events
     */
    tableFormFieldOptOnContentsChanged(event: TableContentsChangedEvent): void {
        this.updateFormFieldRowData();
        this.generateFormDetails();
        this.groupAndValidateTableRows();
    }

    /**
     * Handles table row display events to show/hide fields based on field type
     */
    tableFormFieldOptOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();
        this.configureRowFieldVisibility(tableRow);
    }

    /**
     * Configures the visibility of fields in a table row based on field type and values
     */
    private configureRowFieldVisibility(tableRow: TableRow): void {
        const components = this.getRowComponents(tableRow);

        const isCapType = components.textboxFieldType.text === FIELD_VALUES.CAPTION;
        const isBillOfLadingOnStop = components.textboxFieldValue.text === FIELD_VALUES.BILL_OF_LADING_ON_STOP;

        // Hide field value and length for CAP type
        components.textboxFormFieldName.visible = isCapType || StringUtil.isEmptyString(components.textboxFieldType.text);
        components.textboxFieldValue.visible = !isCapType;
        components.textboxFieldLength.visible = !isCapType;
        components.textboxFieldLength.required = !isCapType;

        // Show additional value only for Bill of Lading on Stop
        components.textboxAdditionalValue.visible = isBillOfLadingOnStop;
    }

    /**
     * Retrieves UI components from a table row
     */
    private getRowComponents(tableRow: TableRow) {
        return {
            textboxFormFieldName: tableRow.findComponentById(COMPONENT_IDS.TEXTBOX_FORM_FIELD_NAME) as Textbox,
            textboxFieldType: tableRow.findComponentById(COMPONENT_IDS.TEXTBOX_FIELD_TYPE) as Textbox,
            textboxFieldValue: tableRow.findComponentById(COMPONENT_IDS.TEXTBOX_FIELD_VALUE) as Textbox,
            textboxFieldLength: tableRow.findComponentById(COMPONENT_IDS.TEXTBOX_FIELD_LENGTH) as Textbox,
            textboxAdditionalValue: tableRow.findComponentById(COMPONENT_IDS.TEXTBOX_ADDITIONAL_VALUE) as Textbox,
            switchRowBreak: tableRow.findComponentById(COMPONENT_IDS.SWITCH_ROW_BREAK) as Switch
        };
    }

    // Dropdown Event Handlers

    /**
     * Handles dropdown selection changes for field values
     */
    textboxFieldValueAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const textbox = event.target as Textbox;
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);

        const textboxAdditionalValue = tableRow.findComponentById(COMPONENT_IDS.TEXTBOX_ADDITIONAL_VALUE) as Textbox;
        if (textboxAdditionalValue) {
            textboxAdditionalValue.visible = event.selection != null && event.selection.displayValue === FIELD_VALUES.BILL_OF_LADING_ON_STOP;
        }
    }

    /**
     * Handles dropdown selection changes for field types, configuring row component visibility and values
     */
    textboxFieldTypeAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const textbox = event.target as Textbox;
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);

        this.handleFieldTypeSelectionChange(tableRow, textbox.text);
    }

    /**
     * Handles field type selection changes by updating component visibility and clearing values
     */
    private handleFieldTypeSelectionChange(tableRow: TableRow, selectedFieldType: string): void {
        const components = this.getRowComponents(tableRow);
        const isCaption = this.isCaptionFieldType(selectedFieldType);

        this.configureFieldVisibilityForType(components, isCaption);
        this.clearFieldValuesForCaptionTypes(components, isCaption);
        this.configureRowBreakForType(components.switchRowBreak, isCaption);
    }

    /**
     * Determines if the selected field type is a caption
     */
    private isCaptionFieldType(fieldType: string): boolean {
        return fieldType === FIELD_VALUES.CAPTION;
    }

    /**
     * Configures component visibility based on whether the field type is caption
     */
    private configureFieldVisibilityForType(components: any, isCaption: boolean): void {
        if (components.textboxFormFieldName) {
            components.textboxFormFieldName.visible = isCaption;
        }

        if (components.textboxFieldValue) {
            components.textboxFieldValue.visible = !isCaption;
        }

        if (components.textboxFieldLength) {
            components.textboxFieldLength.visible = !isCaption;
            components.textboxFieldLength.required = !isCaption;
        }
    }

    /**
     * Clears field values for non-caption field types
     */
    private clearFieldValuesForCaptionTypes(components: any, isCaption: boolean): void {
        if (isCaption) {
            this.clearTextboxValue(components.textboxFormFieldName);
            this.clearTextboxValue(components.textboxFieldValue);
            this.clearTextboxValue(components.textboxAdditionalValue);
            this.clearTextboxValue(components.textboxFieldLength);
        }
    }

    /**
     * Safely clears a textbox value if the textbox exists
     */
    private clearTextboxValue(textbox: Textbox | null): void {
        textbox?.clear();
    }

    /**
     * Configures the row break switch based on field type
     */
    private configureRowBreakForType(switchRowBreak: Switch | null, isCaption: boolean): void {
        if (switchRowBreak) {
            switchRowBreak.checked = !isCaption;
        }
    }

    /**
     * Handles data source execution preparation, applying URL filters for search operations
     */
    sourceMobileCommFormBeforeExecution(event: DataSourceExecutionEvent): void {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.applyUrlFiltersToSearch(event);
        }
    }

    /**
     * Applies URL parameters as filters to search events
     */
    private applyUrlFiltersToSearch(event: DataSourceExecutionEvent): void {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (!urlProps) return;

        if (urlProps.formType != null) {
            event.filter.form_type = urlProps.formType;
        }
        if (urlProps.mcControlId != null) {
            event.filter.mc_control_id = urlProps.mcControlId;
        }
    }

    /**
     * Handles completion of form field options data source execution
     */
    sourceMobileCommFormFieldOptAfterExecution(event: DataSourceExecutionEvent): void {
        if (this.mainDataSource) {
            this.generateFormDetails();
        }
    }

    /**
     * Handles vendor lookup model search preparation
     */
    textboxVendorBeforeLookupModelSearch(event: any): void {
        event.filter.is_active = 'Y';
    }

    /**
     * Handles vendor lookup model selection
     */
    async textboxVendorOnLookupModelSelection(event: any): Promise<void> {
        // Check for duplicate form records when vendor is selected
        if (this.textboxVendor.text && await this.checkForDuplicateFormRecord()) {
            this.mainDataSource?.activeRow?.set("mc_control_id", "");
            this.textboxVendor.focus();
            this.textboxVendor.validationWarning = "Duplicate form record found";
        } else {
            this.textboxVendor.validationWarning = "";
        }
    }

    /**
     * Handles blur event for textbox ID
     */
    async textboxIdOnBlur(event: any): Promise<void> {
        // Check for duplicate form records when ID field loses focus
        if (this.textboxId.text && await this.checkForDuplicateFormRecord()) {
            this.mainDataSource?.activeRow?.set("id", "");
            await this.textboxId.focus();
            this.textboxId.validationWarning = "Duplicate form record found";
        } else {
            this.textboxId.validationWarning = "";
        }
    }

    /**
     * Handles blur event for textbox Form Type
     */
    async textboxFormTypeAfterDropdownSelection(event: DropdownSelectionEvent) {
        // Check for duplicate form records when Form Type field loses focus
        if (this.textboxFormType.text && await this.checkForDuplicateFormRecord()) {
            this.mainDataSource?.activeRow?.set("form_type", "");
            this.textboxFormType.focus();
            this.textboxFormType.validationWarning = "Duplicate form record found";
        } else {
            this.textboxFormType.validationWarning = "";
        }
    }

    /**
     * Checks if a form record already exists with the same id, form_type, and mc_control_id values
     * and displays a dialog if a duplicate is found
     */
    private async checkForDuplicateFormRecord(): Promise<boolean> {
        if (!this.mainDataSource?.activeRow) {
            console.warn('No active row found in main data source');
            return false;
        }

        const activeRow = this.mainDataSource.activeRow;
        const id = activeRow.get("id");
        const formType = activeRow.get("form_type");
        const mcControlId = activeRow.get("mc_control_id");

        // Skip check if any required values are missing
        if (id === undefined || !formType || !mcControlId) {
            return false;
        }

        try {
            const searchParams = {
                id: id,
                form_type: formType,
                mc_control_id: mcControlId
            };

            const response = await Api.search("lme/dispatch/mobilecomm/mc-form", searchParams);

            if (response.data && response.data.length > 0) {
                // Record exists - show dialog
                await CommonDialogs.showDialog(
                    `A form record already exists for these combined values:\nForm: ${id}\nForm Type: ${formType}\nVendor: ${mcControlId}`,
                    {
                        title: "Duplicate Form Record",
                        xVisible: false
                    }
                );
                return true;
            }

            return false;
        } catch (error) {
            console.error('Error checking for duplicate form record:', error);
            return false;
        }
    }

    /**
     * Generates and displays form details based on current table rows
     */
    private generateFormDetails(): void {
        if (!this.mainDataSource || !this.panelFormDisplay || !this.formFieldRenderer) {
            return;
        }

        try {
            this.panelFormDisplay.components = this.formFieldRenderer.renderFormFieldsFromTableRow(this.tableFormFieldOpt.allRows);
        } catch (error) {
            console.error('Error generating form details:', error);
        }
    }

    /**
     * Updates form field row data with control IDs and forced field numbers
     */
    private updateFormFieldRowData(): void {
        if (!this.mainDataSource?.activeRow) {
            console.warn('No active row found in main data source');
            return;
        }

        const mainRow = this.mainDataSource.activeRow;
        const mcControlId = mainRow.get("mc_control_id");
        const formId = mainRow.get("id");

        for (const row of this.tableFormFieldOpt.allRows) {
            this.updateSingleRowData(row, mcControlId, formId);
        }
    }

    /**
     * Updates data for a single table row
     */
    private updateSingleRowData(row: TableRow, mcControlId: any, formId: any): void {
        const rowData = row.data;

        // Set basic form identifiers
        rowData.set("mc_control_id", mcControlId);
        rowData.set("form_id", formId);

        // Set forced field data
        rowData.set("forced_x_placement", this.parseNumberValue(rowData.get("x_placement")));
        rowData.set("forced_y_placement", this.parseNumberValue(rowData.get("y_placement")));
        rowData.set("forced_row_break", rowData.get("row_break"));
        rowData.set("forced_field_type", rowData.get("field_type"));
        rowData.set("forced_field_text", rowData.get("text"));
        rowData.set("forced_field_length", this.parseNumberValue(rowData.get("length")));
        rowData.set("forced_field_value", rowData.get("field_value"));
        rowData.set("forced_reference_qual", rowData.get("reference_qual"));

        // Set field number for non-CAP types
        if (rowData.get("field_type") !== FIELD_TYPES.CAP) {
            this.setForcedFieldNumber(row, rowData);
        }
    }

    /**
     * Safely parses a value to integer, handling both string and number inputs
     */
    private parseNumberValue(value: any): number {
        if (value === null || value === undefined || value === '') {
            return 0;
        }

        const parsed = parseInt(value);
        return isNaN(parsed) ? 0 : parsed;
    }

    /**
     * Generates a 32-character unique identifier
     * @returns A 32-character alphanumeric string
     */
    private generateUniqueId(): string {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < 32; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }

    /**
     * Sets the forced field number for a row
     */
    private setForcedFieldNumber(row: TableRow, rowData: any): void {
        const uniqueId = rowData.get("unique_id");
        const forcedFieldNumber = this.findRowCountByUniqueId(uniqueId);

        if (forcedFieldNumber === -1) {
            rowData.set("unique_id", this.generateUniqueId());
            rowData.set("forced_field_number", this.countNonCapRows() - 1);
        } else {
            rowData.set("forced_field_number", forcedFieldNumber);
        }
    }

    /**
     * Finds the row with the specified unique_id and returns the count of non-CAP rows encountered before it.
     * @param uniqueId The unique_id to search for
     * @returns The count of rows with type !== "CAP" before the matching row, or -1 if not found
     */
    private findRowCountByUniqueId(uniqueId: string): number {
        if (!uniqueId) {
            console.warn('Unique ID is null or undefined');
            return -1;
        }

        let counter = 0;

        for (const row of this.tableFormFieldOpt.allRows) {
            const rowUniqueId = row.data.get("unique_id");

            if (rowUniqueId === uniqueId) {
                return counter;
            }

            const rowType = row.data.get("field_type");
            if (rowType !== FIELD_TYPES.CAP) {
                counter++;
            }
        }

        console.warn(`Unique ID '${uniqueId}' not found in table rows`);
        return -1;
    }

    /**
     * Counts the total number of rows where field_type is not equal to CAP
     * @returns The number of rows with field_type !== FIELD_TYPES.CAP
     */
    private countNonCapRows(): number {
        if (!this.tableFormFieldOpt || !this.tableFormFieldOpt.allRows) {
            return 0;
        }

        let count = 0;
        for (const row of this.tableFormFieldOpt.allRows) {
            const fieldType = row.data.get("field_type");
            if (fieldType !== FIELD_TYPES.CAP) {
                count++;
            }
        }

        return count;
    }

    /**
     * Groups table rows by row_break and validates x_placement ordering within each group
     * @returns An object containing grouped rows and validation results
     */
    private groupAndValidateTableRows(): { groups: TableRow[][], validationErrors: string[] } {
        if (!this.tableFormFieldOpt || !this.tableFormFieldOpt.allRows) {
            return { groups: [], validationErrors: ['No table rows available'] };
        }

        const groups: TableRow[][] = [];
        const validationErrors: string[] = [];
        let currentGroup: TableRow[] = [];

        for (let i = 0; i < this.tableFormFieldOpt.allRows.length; i++) {
            const row = this.tableFormFieldOpt.allRows[i];
            const rowBreak = row.data.get("row_break");
            const xPlacement = this.parseNumberValue(row.data.get("x_placement"));

            // Add current row to the current group
            currentGroup.push(row);

            // Validate x_placement ordering within the current group
            if (currentGroup.length > 1) {
                const previousRow = currentGroup[currentGroup.length - 2];
                const previousXPlacement = this.parseNumberValue(previousRow.data.get("x_placement"));

                if (xPlacement <= previousXPlacement) {
                    const rowIndex = i + 1; // 1-based index for user-friendly error messages
                    validationErrors.push(
                        `Row ${rowIndex}: x_placement value ${xPlacement} should be greater than previous row's x_placement value ${previousXPlacement}`
                    );
                    Snackbar.showWarningSnackbar(
                        `Row ${rowIndex}: X placement value ${xPlacement} should be greater than previous row's X placement value ${previousXPlacement}`, {persist: false}
                    );
                }
            }

            // Check if this row marks the end of a group
            if (rowBreak === true || rowBreak === 'true' || rowBreak === 'Y') {
                if (currentGroup.length > 0) {
                    groups.push([...currentGroup]);
                    currentGroup = [];
                }
            }
        }

        // Add any remaining rows as the final group if not empty
        if (currentGroup.length > 0) {
            groups.push(currentGroup);
        }

        return { groups, validationErrors };
    }

    buttonCopyOnClick() {
        const formLayout = Layout.getLayout("lme/dispatch/mobilecomm/MobileCommForm") as MobileCommForm;
        const formPanel = new CrudDecorator({
            layout: formLayout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            },
            doAfterSlideIn: async () => {
                await formLayout.sourceMobileCommForm?.search({
                    id: this.activeRow.get("id"),
                    mc_control_id: this.activeRow.get("mc_control_id"),
                    form_type: this.activeRow.get("form_type")
                });

                formLayout.sourceMobileCommFormFieldOpt?.search({
                    mc_control_id: this.activeRow.get("mc_control_id"),
                    form_id: this.activeRow.get("id")
                });

                formLayout.sourceMobileCommForm.setRowsAndMode(DataSourceMode.ADD, [formLayout.sourceMobileCommForm.activeRow]);
                formLayout.sourceMobileCommForm.activeRow.set("mc_control_id", null);
                formLayout.sourceMobileCommForm.activeRow.set("creation_date", new Date());
                formLayout.textboxVendor.focus();
                formLayout.sourceMobileCommForm.activeRow._appending = true

                formLayout.sourceMobileCommForm.activeRow.addLinkedModel({
                    model: formLayout.sourceMobileCommFormFieldOpt.url,
                    rows: formLayout.sourceMobileCommFormFieldOpt.data
                });

                formLayout.sourceMobileCommForm.activeRow.addBeforePostListener(async postEvent => {
                    const mcControlId = formLayout.sourceMobileCommForm.activeRow.get("mc_control_id");
                    const formId = formLayout.sourceMobileCommForm.activeRow.get("id");

                    for (const row of formLayout.tableFormFieldOpt.allRows) {
                        row.data._appending = true;
                        row.data.set("id", null);
                        this.updateSingleRowData(row, mcControlId, formId);
                    }
                });

                formLayout.buttonCopy.visible = false;
            },
            doAfterSlideOut: async () => {
                const formId = formLayout.mainDataSource?.activeRow.get("id");
                const formType = formLayout.mainDataSource?.activeRow.get("form_type");
                const vendor = formLayout.mainDataSource?.activeRow.get("mc_control_id");
                // I would rather determine if the slideout was committed vs closed, and only update if committed
                // But doing it this way, for now, will have to suffice
                if (formId != null && formType != null && vendor != null) {
                    await this.updateTables(formId, formType, vendor);
                }
            }
        });

        formPanel.slideIn({ speed: 200 });
    }

    private async updateTables(formId?: any, formType?: any, vendor?: any) {
        if (this.owner) {
            await this.owner.layoutFormGroupByTable.tableFormGroupBy.dataSource.search(this.owner.layoutFormGroupByTable.tableFormGroupBy.dataSource.lastSearch);
            await new Promise(resolve => setTimeout(resolve, 1000));
            await this.selectRowByFormId(formId, formType, vendor);
        }
    }

    async selectRowByFormId(formId: any, formType: any, vendor: any): Promise<void> {
        const tableFormGroupByAll = this.owner?.layoutFormGroupByTable?.tableFormGroupByAll;

        // Return early if any required parameter is null or undefined
        if (tableFormGroupByAll == null || tableFormGroupByAll === undefined) {
            return;
        }
        if (formId == null || formId === undefined) {
            return;
        }
        if (formType == null || formType === undefined) {
            return;
        }
        if (vendor == null || vendor === undefined) {
            return;
        }

        for (let i = 0; i < tableFormGroupByAll.rows.length; i++) {
            const row = tableFormGroupByAll.rows[i];
            if (row.data.get("id") == formId && row.data.get("form_type") == formType && row.data.get("form_vendor") == vendor) {
                tableFormGroupByAll.selectedIndex = i;
                return;
            }
        }
    }

    /**
     * Initializes a blank row for editing when no active row exists in the layout
     * @param layout The layout containing the data source
     * @param sourceRow The source row from which to copy data
     */
    private async initializeBlankRowForEdit(layout: any, sourceRow: any): Promise<void> {
        if (!sourceRow) return;

        const mcControlId = sourceRow.get("mc_control_id");
        const id = sourceRow.get("id");
        const formType = sourceRow.get("form_type");
        const title = sourceRow.get("title");

        const row = await layout.mainDataSource.createBlankRow();
        row.set("mc_control_id", mcControlId);

        // Fetch and set vendor name lookup data
        const controlResponse = await Api.search("lme/dispatch/mobilecomm/mc-control", {
            id: mcControlId
        });

        if (controlResponse.data[0]?.vendor_name) {
            row.setLookupModelData("mc_control_id", {
                id: mcControlId,
                vendor_name: controlResponse.data[0].vendor_name
            });
        }

        // Set basic row properties
        row.set("id", id);
        row.set("form_type", formType);
        row.setLookupModelData("id", {
            id,
            title,
            mc_control_id: mcControlId
        });

        // Fetch and refresh form fields
        const formResponse = await Api.search("lme/dispatch/mobilecomm/mc-form", {
            id,
            mc_control_id: mcControlId,
            form_type: "I"
        });

        if (formResponse.data.length > 0) {
            await layout.refreshFormFieldTable(formResponse.data[0].form_fields);
        }

        layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [row]);
    }

    /** This is an event handler for the afterExecution event of sourceMobileCommForm. */
    sourceMobileCommFormAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.initializeSelectionTable();
        }
    }

    private initializeSelectionTable(): void {
        if (this.mainDataSource?.activeRow?.get("form_type") == "I") {
            this.buttonExApp.enabled = true;
            this.layoutExAppSelectionList.visible = true;

            const layout = Layout.getLayout("lme/dispatch/mobilecomm/MobileCommExAppControl") as MobileCommExAppControl;
            const crud = new CrudDecorator({
                layout,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true},
                    doAfterSlideOut: async () => {
                        this.layoutExAppSelectionList.mainDataSource?.search(this.layoutExAppSelectionList.mainDataSource.lastSearch);
                    }
            });

            const editClickListener = async (event) => {
                crud.slideIn({ speed: 200, direction: Alignment.BOTTOM});

                const sourceRow = this.mainDataSource?.activeRow;
                await layout.mainDataSource?.search({
                    id: sourceRow?.get("id"),
                    mc_control_id: sourceRow?.get("mc_control_id")
                });

                if (!layout.mainDataSource.activeRow) {
                    await this.initializeBlankRowForEdit(layout, sourceRow);
                }

                layout.textboxMcControlId.enabled = false;
            };

            if (this._editClickListener) {
                this.layoutExAppSelectionList.buttonEdit.removeClickListener(this._editClickListener);
            }

            this._editClickListener = editClickListener;
            this.layoutExAppSelectionList.buttonEdit.addClickListener(this._editClickListener);

            this.layoutExAppSelectionList.mainDataSource?.search(
                {
                    form_id: this.mainDataSource?.activeRow?.get("id"),
                    mc_control_id: this.mainDataSource?.activeRow?.get("mc_control_id")
                }
            );
        } else {
            this.buttonExApp.enabled = false;
            this.layoutExAppSelectionList.visible = false;
        }
    }

    buttonExAppOnClick() {
        const id = this.mainDataSource?.activeRow?.get("id");
        const mcControlId = this.mainDataSource?.activeRow?.get("mc_control_id");
        Navigation.navigateTo(`lme/dispatch/mobilecomm/MobileCommExAppControl?id=${id}&mc_control_id=${mcControlId}`, {newTab: true});
    }
}
